import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Employee, LeaveRequest, AttendanceRecord, PayrollRecord, Job, Candidate, PerformanceRecord, TrainingProgram, Department, OnboardingEmployee, Notification, User, OnboardingConfig, Role, Holiday, Shift, RosterEntry } from '../../types';
import { apiClient } from '../../api/apiClient';

interface DataState {
  users: User[];
  employees: Employee[];
  leaveRequests: LeaveRequest[];
  attendance: AttendanceRecord[];
  payroll: PayrollRecord[];
  jobs: Job[];
  candidates: Candidate[];
  performance: PerformanceRecord[];
  performanceStats: { avgScore: string; reviewsDone: number; topPerformers: number; pendingReviews: number } | null;
  okrs: any[];
  training: TrainingProgram[];
  departments: Department[];
  onboarding: OnboardingEmployee[];
  onboardingConfig: OnboardingConfig | null;
  notifications: Notification[];
  sysRoles: { v: string, l: string }[];
  roles: Role[];
  holidays: Holiday[];
  settings: any | null;
  loading: boolean;
  error: string | null;
  reportData: { headers: string[]; rows: any[][]; summary: any; csv: string } | null;
  reportLoading: boolean;
  companies: any[];
  goals: any[];
  performanceQuestions: any[];
  performanceReviews: any[];
  shifts: Shift[];
  rosters: RosterEntry[];
  branches: any[];
}

const initialState: DataState = {
  users: [], employees: [], leaveRequests: [], attendance: [], payroll: [],
  jobs: [], candidates: [], performance: [], performanceStats: null, okrs: [], training: [], departments: [],
  onboarding: [], onboardingConfig: null, notifications: [], sysRoles: [], roles: [], holidays: [], settings: null, loading: false, error: null,
  reportData: null, reportLoading: false, companies: [],
  goals: [], performanceQuestions: [], performanceReviews: [], shifts: [], rosters: [], branches: [],
};

// Fetch all data dynamically from API
export const fetchAllData = createAsyncThunk('data/fetchAll', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get('/data/all');
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch data');
  }
});

export const fetchEmployeesAsync = createAsyncThunk('data/fetchEmployees', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get('/employees');
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch employees');
  }
});

export const fetchCompaniesAsync = createAsyncThunk('data/fetchCompanies', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get('/companies');
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch companies');
  }
});

export const deleteCompanyAsync = createAsyncThunk('data/deleteCompany', async (id: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/companies/${id}`);
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to delete company');
  }
});

export const updateCompanyAsync = createAsyncThunk('data/updateCompany', async ({ id, payload }: { id: string, payload: any }, { rejectWithValue }) => {
  try {
    const res = await apiClient.put(`/companies/${id}`, payload);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to update company');
  }
});

export const registerCompanyAsync = createAsyncThunk('data/registerCompany', async (payload: any, { rejectWithValue }) => {
  try {
    const res = await apiClient.post('/companies/register', payload);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to register company');
  }
});

// Branch Thunks
export const fetchBranchesAsync = createAsyncThunk('data/fetchBranches', async (companyId: string | undefined, { rejectWithValue }) => {
  try {
    const url = companyId ? `/branches?companyId=${companyId}` : '/branches';
    const res = await apiClient.get(url);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch branches');
  }
});

export const createBranchAsync = createAsyncThunk('data/createBranch', async (payload: any, { rejectWithValue }) => {
  try {
    const res = await apiClient.post('/branches', payload);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to create branch');
  }
});

export const updateBranchAsync = createAsyncThunk('data/updateBranch', async ({ id, payload }: { id: string, payload: any }, { rejectWithValue }) => {
  try {
    const res = await apiClient.put(`/branches/${id}`, payload);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to update branch');
  }
});

export const deleteBranchAsync = createAsyncThunk('data/deleteBranch', async (id: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/branches/${id}`);
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to delete branch');
  }
});

// Role Thunks
export const fetchRolesAsync = createAsyncThunk('data/fetchRoles', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get('/roles');
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch roles');
  }
});

export const createRoleAsync = createAsyncThunk('data/createRole', async (role: Role, { rejectWithValue }) => {
  try {
    const res = await apiClient.post('/roles', role);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to create role');
  }
});

export const updateRoleAsync = createAsyncThunk('data/updateRole', async (role: Role, { rejectWithValue }) => {
  try {
    const res = await apiClient.put(`/roles/${role._id || role.id}`, role);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to update role');
  }
});

export const deleteRoleAsync = createAsyncThunk('data/deleteRole', async (id: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/roles/${id}`);
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to delete role');
  }
});

// Holiday Thunks
export const addHolidayAsync = createAsyncThunk('data/addHoliday', async (holiday: Holiday, { rejectWithValue }) => {
  try {
    const res = await apiClient.post('/holidays', holiday);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to add holiday');
  }
});

export const deleteHolidayAsync = createAsyncThunk('data/deleteHoliday', async (id: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/holidays/${id}`);
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to delete holiday');
  }
});

// Shift Thunks
export const fetchShiftsAsync = createAsyncThunk('data/fetchShifts', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get('/shifts');
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch shifts');
  }
});

export const createShiftAsync = createAsyncThunk('data/createShift', async (shift: Partial<Shift>, { rejectWithValue }) => {
  try {
    const res = await apiClient.post('/shifts', shift);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to create shift');
  }
});

export const updateShiftAsync = createAsyncThunk('data/updateShift', async (shift: Partial<Shift>, { rejectWithValue }) => {
  try {
    const res = await apiClient.put(`/shifts/${shift._id || shift.id}`, shift);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to update shift');
  }
});

export const deleteShiftAsync = createAsyncThunk('data/deleteShift', async (id: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/shifts/${id}`);
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to delete shift');
  }
});

// Roster Thunks
export const fetchRostersAsync = createAsyncThunk('data/fetchRosters', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get('/rosters');
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch rosters');
  }
});

export const createRosterAsync = createAsyncThunk('data/createRoster', async (roster: Partial<RosterEntry>, { rejectWithValue }) => {
  try {
    const res = await apiClient.post('/rosters', roster);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to create roster');
  }
});

export const updateRosterAsync = createAsyncThunk('data/updateRoster', async (roster: Partial<RosterEntry>, { rejectWithValue }) => {
  try {
    const res = await apiClient.put(`/rosters/${roster._id || roster.id}`, roster);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to update roster');
  }
});

export const deleteRosterAsync = createAsyncThunk('data/deleteRoster', async (id: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/rosters/${id}`);
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to delete roster');
  }
});

// Example Async Thunk: Create Employee
export const createEmployeeAsync = createAsyncThunk(
  'data/createEmployee',
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/employees', payload);
      return res.data; // Assuming backend returns the created employee with ID
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create employee');
    }
  }
);

// Example Async Thunk: Update Leave Status
export const updateLeaveStatusAsync = createAsyncThunk(
  'data/updateLeaveStatus',
  async ({ id, status, forwardedTo, comment }: { id: string; status: string; forwardedTo?: string; comment?: string }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/leaves/${id}/status`, { status, forwardedTo, comment });
      return res.data; // Assuming backend returns updated leave request
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update leave request');
    }
  }
);

export const removeEmployeeAsync = createAsyncThunk(
  'data/removeEmployee',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/employees/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete employee');
    }
  }
);

export const updateEmployeeAsync = createAsyncThunk(
  'data/updateEmployee',
  async ({ id, payload }: { id: string; payload: any }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/employees/${id}`, payload);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update employee');
    }
  }
);

export const addLeaveRequestAsync = createAsyncThunk(
  'data/addLeaveRequest',
  async (leave: Omit<LeaveRequest, 'id'>, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const currentUserId = state.auth.user?._id;
      // Find the employee ObjectId associated with this user
      const emp = state.data.employees.find((e: any) => e.user?._id === currentUserId || e.user === currentUserId);
      const employeeObjectId = emp ? (emp._id || emp.id) : leave.empId;

      // Map to backend schema
      // Strip duration suffix e.g. "Casual (First Half)" → "Casual"
      const baseLeaveType = (leave.type || '').replace(/\s*\(.*?\)/, '').trim();

      const payload = {
        employee: employeeObjectId,
        leaveType: baseLeaveType,
        duration: leave.duration || 'Full Day',
        days: leave.days,
        startDate: leave.startDate,
        endDate: leave.endDate,
        reason: leave.reason
      };

      const res = await apiClient.post('/leaves', payload);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add leave request');
    }
  }
);

export const addDepartmentAsync = createAsyncThunk(
  'data/addDepartment',
  async (dept: any, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/departments', dept);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add department');
    }
  }
);

export const deleteDepartmentAsync = createAsyncThunk(
  'data/deleteDepartment',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/departments/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete department');
    }
  }
);

export const updateDepartmentAsync = createAsyncThunk(
  'data/updateDepartment',
  async (dept: any, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/departments/${dept.id || dept._id}`, dept);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update department');
    }
  }
);

export const fetchDepartmentsAsync = createAsyncThunk(
  'data/fetchDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get('/departments');
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch departments');
    }
  }
);

export const updateProfileAsync = createAsyncThunk(
  'data/updateProfile',
  async (profile: any, { rejectWithValue }) => {
    try {
      const isFormData = profile instanceof FormData;
      const res = await apiClient.put('/employees/profile', profile, {
        headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update profile');
    }
  }
);

export const addJobAsync = createAsyncThunk(
  'data/addJob',
  async (job: Omit<Job, 'id'>, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/jobs', job);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add job');
    }
  }
);

export const updateJobAsync = createAsyncThunk(
  'data/updateJob',
  async ({ id, payload }: { id: string; payload: Partial<Job> }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/jobs/${id}`, payload);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update job');
    }
  }
);

export const deleteJobAsync = createAsyncThunk(
  'data/deleteJob',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/jobs/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete job');
    }
  }
);


export const addCandidateAsync = createAsyncThunk(
  'data/addCandidate',
  async (candidate: any, { rejectWithValue }) => {
    try {
      const isFormData = candidate instanceof FormData;
      const res = await apiClient.post('/candidates', candidate, {
        headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add candidate');
    }
  }
);

export const moveCandidateStageAsync = createAsyncThunk(
  'data/moveCandidateStage',
  async ({ id, stage }: { id: string; stage: string }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/candidates/${id}/stage`, { stage });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update candidate stage');
    }
  }
);

export const deleteCandidateAsync = createAsyncThunk(
  'data/deleteCandidate',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/candidates/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete candidate');
    }
  }
);

// Onboarding — create a new joinee record directly (not via candidate
// pipeline move). Adjust the endpoint path if your backend route differs.
export const createOnboardingAsync = createAsyncThunk(
  'data/createOnboarding',
  async (payload: Partial<OnboardingEmployee>, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/onboarding', payload);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add joiner');
    }
  }
);

// Onboarding — update an existing joiner's core details (name, role, dept,
// joinDate, buddy) via the Edit action in the New Joiners table.
export const updateOnboardingAsync = createAsyncThunk(
  'data/updateOnboarding',
  async ({ id, payload }: { id: string; payload: Partial<OnboardingEmployee> }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/onboarding/${id}`, payload);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update joiner');
    }
  }
);

// Onboarding — permanently remove a joiner record via the Delete action in
// the New Joiners table.
export const deleteOnboardingAsync = createAsyncThunk(
  'data/deleteOnboarding',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/onboarding/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete joiner');
    }
  }
);

export const moveOnboardingStageAsync = createAsyncThunk(
  'data/moveOnboardingStage',
  async ({ id, stage }: { id: string; stage: string }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/onboarding/${id}/stage`, { stage });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update onboarding stage');
    }
  }
);

export const toggleOnboardingTaskAsync = createAsyncThunk(
  'data/toggleOnboardingTask',
  async ({ id, taskIdx }: { id: string; taskIdx: number }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/onboarding/${id}/task`, { taskIdx });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to toggle task');
    }
  }
);

export const toggleHrChecklistAsync = createAsyncThunk(
  'data/toggleHrChecklist',
  async (taskIdx: number, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/onboarding/config/checklist/${taskIdx}`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to toggle HR checklist task');
    }
  }
);

export const markNotificationReadAsync = createAsyncThunk(
  'data/markNotificationRead',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/notifications/${id}/read`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to mark notification read');
    }
  }
);

export const clearNotificationsAsync = createAsyncThunk(
  'data/clearNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.delete(`/notifications/clear-all`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to clear notifications');
    }
  }
);

export const addTrainingProgramAsync = createAsyncThunk(
  'data/addTrainingProgram',
  async (program: any, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/trainings', program);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create training program');
    }
  }
);

export const enrollTrainingAsync = createAsyncThunk(
  'data/enrollTraining',
  async (trainingId: string, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const currentUserId = state.auth.user?._id;
      const emp = state.data.employees.find((e: any) => e.user?._id === currentUserId || e.user === currentUserId);
      const employeeId = emp ? (emp._id || emp.id) : currentUserId;

      const res = await apiClient.post(`/trainings/${trainingId}/enroll`, { employeeId });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to enroll');
    }
  }
);

export const updateTrainingProgressAsync = createAsyncThunk(
  'data/updateTrainingProgress',
  async (trainingId: string, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const currentUserId = state.auth.user?._id;
      const emp = state.data.employees.find((e: any) => e.user?._id === currentUserId || e.user === currentUserId);
      const employeeId = emp ? (emp._id || emp.id) : currentUserId;

      const res = await apiClient.put(`/trainings/${trainingId}/progress`, { employeeId });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update progress');
    }
  }
);

export const attendanceActionAsync = createAsyncThunk(
  'data/attendanceAction',
  async ({ employeeId, action }: { employeeId: string; action: 'check-in' | 'start-break' | 'end-break' | 'check-out' }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/attendance/action', { employeeId, action });
      return { record: res.data, action };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || `Failed to ${action}`);
    }
  }
);

export const runPayrollAsync = createAsyncThunk(
  'data/runPayroll',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/payroll/run-bulk');
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to run payroll');
    }
  }
);

export const payPayrollAsync = createAsyncThunk(
  'data/payPayroll',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/payroll/${id}/pay`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to mark payroll as paid');
    }
  }
);

export const updateSettingsAsync = createAsyncThunk(
  'data/updateSettings',
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await apiClient.put('/settings', payload);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update settings');
    }
  }
);

export const addOkrAsync = createAsyncThunk(
  'data/addOkr',
  async (okr: any, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/performance/okr', okr);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add OKR');
    }
  }
);

export const updatePerformanceAsync = createAsyncThunk(
  'data/updatePerformance',
  async (perf: { id: string; score: number; rating: number; goals: string }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/performance/${perf.id}`, perf);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update performance');
    }
  }
);

export const startNewCycleAsync = createAsyncThunk(
  'data/startNewCycle',
  async (quarter: string, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/performance/cycle', { quarter });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to start new cycle');
    }
  }
);

// --- Goals Thunks ---
export const fetchGoalsAsync = createAsyncThunk('data/fetchGoals', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get('/goals');
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch goals');
  }
});
export const createGoalAsync = createAsyncThunk('data/createGoal', async (payload: any, { rejectWithValue }) => {
  try {
    const res = await apiClient.post('/goals', payload);
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to create goal');
  }
});
export const updateGoalAsync = createAsyncThunk('data/updateGoal', async ({ id, payload }: { id: string; payload: any }, { rejectWithValue }) => {
  try {
    const res = await apiClient.put(`/goals/${id}`, payload);
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to update goal');
  }
});
export const updateGoalApprovalAsync = createAsyncThunk('data/updateGoalApproval', async ({ id, payload }: { id: string; payload: any }, { rejectWithValue }) => {
  try {
    const res = await apiClient.put(`/goals/${id}/approval`, payload);
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to update goal approval');
  }
});
export const deleteGoalAsync = createAsyncThunk('data/deleteGoal', async (id: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/goals/${id}`);
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to delete goal');
  }
});
export const uploadGoalCertificateAsync = createAsyncThunk('data/uploadGoalCertificate', async ({ id, formData }: { id: string; formData: FormData }, { rejectWithValue }) => {
  try {
    const res = await apiClient.put(`/goals/${id}/certificate`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to upload certificate');
  }
});
export const verifyGoalCertificateAsync = createAsyncThunk('data/verifyGoalCertificate', async ({ id, payload }: { id: string; payload: any }, { rejectWithValue }) => {
  try {
    const res = await apiClient.put(`/goals/${id}/verify-certificate`, payload);
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to verify certificate');
  }
});

// --- Performance Questions Thunks ---
export const fetchQuestionsAsync = createAsyncThunk('data/fetchQuestions', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get('/reviews/questions');
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch questions');
  }
});
export const createQuestionAsync = createAsyncThunk('data/createQuestion', async (payload: any, { rejectWithValue }) => {
  try {
    const res = await apiClient.post('/reviews/questions', payload);
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to create question');
  }
});
export const updateQuestionAsync = createAsyncThunk('data/updateQuestion', async ({ id, payload }: { id: string; payload: any }, { rejectWithValue }) => {
  try {
    const res = await apiClient.put(`/reviews/questions/${id}`, payload);
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to update question');
  }
});
export const deleteQuestionAsync = createAsyncThunk('data/deleteQuestion', async (id: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/reviews/questions/${id}`);
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to delete question');
  }
});

// --- Monthly Reviews Thunks ---
export const fetchReviewsAsync = createAsyncThunk('data/fetchReviews', async (filters: any, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams(filters).toString();
    const res = await apiClient.get(`/reviews/reviews?${params}`);
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch reviews');
  }
});
export const submitReviewAsync = createAsyncThunk('data/submitReview', async (payload: any, { rejectWithValue }) => {
  try {
    const res = await apiClient.post('/reviews/reviews', payload);
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to submit review');
  }
});
export const deleteReviewAsync = createAsyncThunk('data/deleteReview', async (id: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/reviews/reviews/${id}`);
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to delete review');
  }
});

// Fetch a specific report from backend
export const fetchReportAsync = createAsyncThunk(
  'data/fetchReport',
  async (type: string, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/reports/${type}`);
      return res.data as { headers: string[]; rows: any[][]; summary: any; csv: string };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch report');
    }
  }
);


const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    // Keep existing synchronous actions for components not yet refactored to use async thunks
    addUser(state, action: PayloadAction<User>) {
      state.users.push(action.payload);

    },
    removeUser(state, action: PayloadAction<string>) {
      state.users = state.users.filter(u => u.email !== action.payload);
    },
    addEmployee(state, action: PayloadAction<Employee>) {
      state.employees.push(action.payload);

    },
    removeEmployee(state, action: PayloadAction<string>) {
      state.employees = state.employees.filter(e => e.id !== action.payload);
    },
    updateLeaveStatus(state, action: PayloadAction<{ id: string; status: string }>) {
      const req = state.leaveRequests.find(r => r.id === action.payload.id);
      if (req) req.status = action.payload.status;
    },
    addLeaveRequest(state, action: PayloadAction<LeaveRequest>) {
      state.leaveRequests.unshift(action.payload);
    },
    addAttendanceRecord(state, action: PayloadAction<AttendanceRecord>) {
      state.attendance.unshift(action.payload);
    },
    addJob(state, action: PayloadAction<Job>) {
      state.jobs.push(action.payload);
    },
    moveCandidateStage(state, action: PayloadAction<{ id: string; stage: string }>) {
      const c = state.candidates.find(c => c.id === action.payload.id);
      if (c) c.stage = action.payload.stage;
    },
    toggleOnboardingTask(state, action: PayloadAction<{ joineeId: string; taskIdx: number }>) {
      const j = state.onboarding.find(o => o.id === action.payload.joineeId);
      if (j) {
        j.tasks[action.payload.taskIdx].done = !j.tasks[action.payload.taskIdx].done;
        j.progress = Math.round((j.tasks.filter(t => t.done).length / j.tasks.length) * 100);
      }
    },
    markNotificationRead(state, action: PayloadAction<string>) {
      const n = state.notifications.find(n => n.id === action.payload);
      if (n) n.read = true;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAllData.pending, state => { state.loading = true; state.error = null; })
      .addCase(fetchAllData.fulfilled, (state, action) => {
        state.loading = false;
        const d = action.payload;
        state.users = (d.users || []).map((u: any) => ({ ...u, email: u.email?.toLowerCase() || '' }));
        state.roles = d.roles || [];
        state.employees = (d.employees || []).map((e: any) => {
          if (e.user && e.user.email) e.user.email = e.user.email.toLowerCase();
          if (e.email) e.email = e.email.toLowerCase();
          e.permissions = e.user?.permissions || e.permissions || [];
          return e;
        });

        state.leaveRequests = (d.leaveRequests || []).map((lr: any) => {
          const start = new Date(lr.startDate);
          const end = new Date(lr.endDate);
          const diffDays = Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
          return {
            id: lr._id || lr.id,
            empId: lr.employee?.id || lr.employee?._id || lr.employee || '',
            name: lr.employee?.name || 'Unknown',
            type: lr.leaveType || lr.type || 'Casual',
            duration: lr.duration || 'Full Day',
            dates: `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`,
            startDate: lr.startDate,
            endDate: lr.endDate,
            days: lr.days || diffDays,
            status: lr.status || 'Pending',
            initials: lr.employee?.initials || '?',
            avColor: lr.employee?.avColor || 'blue',
            reason: lr.reason || '',
            currentApprover: lr.currentApprover,
            approvalChain: lr.approvalChain
          };
        });

        state.payroll = (d.payroll || []).filter((p: any) => p.employee).map((p: any) => ({
          id: p._id || p.id,
          empId: p.employee?._id || p.employee,
          name: p.employee?.name || 'Unknown',
          dept: p.employee?.dept || 'Engineering',
          month: p.month,
          earnings: p.earnings || [],
          deductions: p.deductions || [],
          grossEarnings: p.grossEarnings || 0,
          totalDeductions: p.totalDeductions || 0,
          net: p.netSalary || 0,
          status: p.status || 'Pending',
          paymentDate: p.paymentDate
        }));
        state.jobs = d.recruitment || d.jobs || [];
        state.candidates = d.candidates || [];
        state.performance = d.performance || [];
        state.performanceStats = d.performanceStats || null;
        state.okrs = d.okrs || [];
        state.goals = d.goals || state.goals;
        state.performanceQuestions = d.performanceQuestions || state.performanceQuestions;
        state.performanceReviews = d.performanceReviews || state.performanceReviews;

        state.training = (d.training || []).map((t: any) => ({
          id: t._id || t.id,
          name: t.title,
          category: t.category || 'Technical',
          enrolled: t.enrolledEmployees?.length || 0,
          completed: 0,
          deadline: t.endDate ? new Date(t.endDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '',
          instructor: t.instructor || 'Unknown',
          duration: "10h", // Placeholder or calculate from dates
          status: t.status || 'Upcoming',
          enrolledEmployees: t.enrolledEmployees || [],
          totalModules: t.totalModules || 5
        }));

        state.departments = d.departments || [];
        state.onboarding = d.onboarding || [];
        state.onboardingConfig = d.onboardingConfig || null;
        state.notifications = d.notifications || [];
        state.settings = d.settings || null;
        state.holidays = d.holidays || [];
        state.sysRoles = (d.sysRoles || ['Admin', 'HR', 'Manager', 'Employee']).map((r: string) => ({
          v: r.toLowerCase(),
          l: r
        }));
        state.roles = d.roles || [];

        const formatTime = (dt: any) => {
          if (!dt) return '--:--';
          return new Date(dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        };
        const formatDuration = (secs: number) => {
          const h = Math.floor(secs / 3600);
          const m = Math.floor((secs % 3600) / 60);
          return `${h}h ${m}m`;
        };

        state.attendance = (d.attendance || [])
          .filter((record: any) => record.employee?.name)
          .map((record: any) => {
            const id = record._id || record.id;
            let workSecs = 0;
            if (record.checkIn && record.checkOut) {
              const diffMs = new Date(record.checkOut).getTime() - new Date(record.checkIn).getTime();
              workSecs = Math.max(0, Math.floor(diffMs / 1000) - (record.totalBreakSec || 0));
            }
            return {
              id: id,
              _id: id,
              empId: record.employee?._id || record.employee,
              name: record.employee.name,
              date: new Date(record.date).toISOString().split('T')[0],
              clockIn: formatTime(record.checkIn),
              clockOut: formatTime(record.checkOut),
              workHrs: record.checkOut ? formatDuration(workSecs) : '0h 0m',
              breakHrs: formatDuration(record.totalBreakSec || 0),
              status: record.status || 'Present',
              initials: record.employee?.initials || '?',
              avColor: record.employee?.avColor || 'blue',
              sysId: record.employee?.id || '',
              rawCheckIn: record.checkIn,
              rawCheckOut: record.checkOut,
              breakStart: record.breakStart,
              totalBreakSec: record.totalBreakSec || 0
            };
          });
      })
      .addCase(fetchRolesAsync.fulfilled, (state, action) => {
        state.roles = action.payload;
      })
      .addCase(createRoleAsync.fulfilled, (state, action) => {
        state.roles.push(action.payload);
      })
      .addCase(updateRoleAsync.fulfilled, (state, action) => {
        const index = state.roles.findIndex((r: any) => (r._id && r._id === action.payload._id) || (r.id && r.id === action.payload.id));
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
      })
      .addCase(deleteRoleAsync.fulfilled, (state, action) => {
        state.roles = state.roles.filter((r: any) => r._id !== action.payload && r.id !== action.payload);
      })
      .addCase(addHolidayAsync.fulfilled, (state, action) => {
        state.holidays.push(action.payload);
        state.holidays.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      })
      .addCase(deleteHolidayAsync.fulfilled, (state, action) => {
        state.holidays = state.holidays.filter((h: any) => h._id !== action.payload && h.id !== action.payload);
      })
      .addCase(fetchCompaniesAsync.fulfilled, (state, action) => {
        state.companies = (action.payload || []).map((c: any) => ({ ...c, email: c.email?.toLowerCase() || '' }));
      })
      .addCase(deleteCompanyAsync.fulfilled, (state, action) => {
        state.companies = state.companies.filter((c: any) => c._id !== action.payload && c.id !== action.payload);
      })
      .addCase(updateCompanyAsync.fulfilled, (state, action) => {
        const index = state.companies.findIndex((c: any) => (c._id && c._id === action.payload._id) || (c.id && c.id === action.payload.id));
        if (index !== -1) {
          state.companies[index] = { ...state.companies[index], ...action.payload };
        }
      })
      .addCase(fetchBranchesAsync.fulfilled, (state, action) => {
        state.branches = action.payload;
      })
      .addCase(createBranchAsync.fulfilled, (state, action) => {
        state.branches.unshift(action.payload);
      })
      .addCase(updateBranchAsync.fulfilled, (state, action) => {
        const payloadId = action.payload._id || action.payload.id;
        const idx = state.branches.findIndex((b: any) => (b._id || b.id) === payloadId);
        if (idx !== -1) {
          state.branches[idx] = action.payload;
        }
      })
      .addCase(deleteBranchAsync.fulfilled, (state, action) => {
        state.branches = state.branches.filter((b: any) => b._id !== action.payload && b.id !== action.payload);
      })
      .addCase(fetchGoalsAsync.fulfilled, (state, action) => { state.goals = action.payload; })
      .addCase(createGoalAsync.fulfilled, (state, action) => { state.goals.unshift(action.payload); })
      .addCase(updateGoalAsync.fulfilled, (state, action) => {
        const payloadId = action.payload.id || action.payload._id;
        const idx = state.goals.findIndex(g => (g.id || g._id) === payloadId);
        if (idx !== -1) state.goals[idx] = action.payload;
      })
      .addCase(updateGoalApprovalAsync.fulfilled, (state, action) => {
        const payloadId = action.payload.id || action.payload._id;
        const idx = state.goals.findIndex(g => (g.id || g._id) === payloadId);
        if (idx !== -1) state.goals[idx] = action.payload;
      })
      .addCase(deleteGoalAsync.fulfilled, (state, action) => {
        state.goals = state.goals.filter(g => g.id !== action.payload && g._id !== action.payload);
      })
      .addCase(fetchQuestionsAsync.fulfilled, (state, action) => { state.performanceQuestions = action.payload; })
      .addCase(createQuestionAsync.fulfilled, (state, action) => { state.performanceQuestions.push(action.payload); })
      .addCase(updateQuestionAsync.fulfilled, (state, action) => {
        const payloadId = action.payload.id || action.payload._id;
        const idx = state.performanceQuestions.findIndex(q => (q.id || q._id) === payloadId);
        if (idx !== -1) state.performanceQuestions[idx] = action.payload;
      })
      .addCase(deleteQuestionAsync.fulfilled, (state, action) => {
        state.performanceQuestions = state.performanceQuestions.filter(q => q.id !== action.payload && q._id !== action.payload);
      })
      .addCase(fetchReviewsAsync.fulfilled, (state, action) => { state.performanceReviews = action.payload; })
      .addCase(submitReviewAsync.fulfilled, (state, action) => {
        const payloadId = action.payload.id || action.payload._id;
        const idx = state.performanceReviews.findIndex(r => (r.id || r._id) === payloadId);
        if (idx !== -1) state.performanceReviews[idx] = action.payload;
        else state.performanceReviews.unshift(action.payload);
      })
      .addCase(deleteReviewAsync.fulfilled, (state, action) => {
        state.performanceReviews = state.performanceReviews.filter(r => r.id !== action.payload && r._id !== action.payload);
      })
      .addCase(fetchAllData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchEmployeesAsync handler
      .addCase(fetchEmployeesAsync.fulfilled, (state, action) => {
        state.employees = action.payload.map((e: any) => ({
          ...e,
          permissions: e.user?.permissions || e.permissions || []
        }));
      })
      // createEmployeeAsync handlers
      .addCase(createEmployeeAsync.fulfilled, (state, action) => {
        state.employees.push(action.payload);
      })
      .addCase(updateEmployeeAsync.fulfilled, (state, action) => {
        const updatedEmp = action.payload;
        const idx = state.employees.findIndex((e: any) => (e._id && e._id === updatedEmp._id) || (e.id && e.id === updatedEmp.id));
        if (idx !== -1) {
          // Preserve the populated user object if backend didn't return it populated
          const existingUser = state.employees[idx].user;
          state.employees[idx] = {
            ...updatedEmp,
            permissions: updatedEmp.user?.permissions || updatedEmp.permissions || existingUser?.permissions || []
          };
          if (!updatedEmp.user || typeof updatedEmp.user === 'string') {
            state.employees[idx].user = existingUser;
          }
        }
      })
      .addCase(updateProfileAsync.fulfilled, (state, action) => {
        const updatedEmp = action.payload;
        const idx = state.employees.findIndex((e: any) => (e._id && e._id === updatedEmp._id) || (e.id && e.id === updatedEmp.id));
        if (idx !== -1) {
          const existingUser = state.employees[idx].user;
          state.employees[idx] = updatedEmp;
          if (!updatedEmp.user || typeof updatedEmp.user === 'string') {
            state.employees[idx].user = existingUser;
          }
        }
      })
      // updateLeaveStatusAsync handlers
      .addCase(updateLeaveStatusAsync.fulfilled, (state, action) => {
        const payloadId = action.payload._id || action.payload.id;
        const req = state.leaveRequests.find((r: any) => (r._id || r.id) === payloadId);
        if (req) {
          req.status = action.payload.status;
          req.currentApprover = action.payload.currentApprover;
          req.approvalChain = action.payload.approvalChain;
        }
      })
      .addCase(removeEmployeeAsync.fulfilled, (state, action) => {
        state.employees = state.employees.filter((e: any) => e.id !== action.payload && e._id !== action.payload);
      })
      .addCase(addDepartmentAsync.fulfilled, (state, action) => {
        state.departments.push(action.payload);
      })
      .addCase(updateDepartmentAsync.fulfilled, (state, action) => {
        const idx = state.departments.findIndex((d: any) => (d._id && d._id === action.payload._id) || (d.id && d.id === action.payload.id));
        if (idx !== -1) {
          state.departments[idx] = action.payload;
        }
      })
      .addCase(deleteDepartmentAsync.fulfilled, (state, action) => {
        state.departments = state.departments.filter((d: any) => d.id !== action.payload && d._id !== action.payload);
      })
      .addCase(fetchDepartmentsAsync.fulfilled, (state, action) => {
        state.departments = action.payload;
      })
      .addCase(addLeaveRequestAsync.fulfilled, (state, action) => {
        const lr = action.payload;
        const start = new Date(lr.startDate);
        const end = new Date(lr.endDate);
        const diffDays = Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

        // Backend now returns the populated employee object
        const empObj = typeof lr.employee === 'object' ? lr.employee : null;
        // Fallback: find in state by _id if not populated
        const empInState = !empObj
          ? state.employees.find((e: any) => e._id === lr.employee || e.id === lr.employee)
          : null;
        const resolvedEmp = empObj || empInState;

        // In local state, push a frontend-formatted object so UI updates instantly
        state.leaveRequests.unshift({
          id: lr._id || lr.id,
          // Use human-readable empId (e.g. "EMP001") not MongoDB ObjectId
          empId: resolvedEmp?.id || resolvedEmp?.empId || (typeof lr.employee === 'string' ? lr.employee : ''),
          name: resolvedEmp?.name || 'Self',
          type: lr.leaveType || lr.type,
          duration: lr.duration || 'Full Day',
          dates: `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`,
          startDate: lr.startDate,
          endDate: lr.endDate,
          days: lr.days || diffDays,
          status: lr.status || 'Pending',
          initials: resolvedEmp?.initials || '?',
          avColor: resolvedEmp?.avColor || 'blue',
          reason: lr.reason || '',
          currentApprover: lr.currentApprover || null,
          approvalChain: lr.approvalChain || []
        });
      })
      .addCase(addJobAsync.fulfilled, (state, action) => {
        if (action.payload.job) {
          state.jobs.push(action.payload.job);
        } else {
          state.jobs.push(action.payload);
        }
        if (action.payload.notification) {
          state.notifications.unshift(action.payload.notification);
        }
      })

      .addCase(updateJobAsync.fulfilled, (state, action) => {
  const updatedId = action.payload._id || action.payload.id;
  const idx = state.jobs.findIndex((j: any) => (j._id || j.id) === updatedId);
  if (idx !== -1) {
    state.jobs[idx] = action.payload;
  }
})
.addCase(deleteJobAsync.fulfilled, (state, action) => {
  state.jobs = state.jobs.filter(
    (j: any) => j.id !== action.payload && j._id !== action.payload,
  );
})
      .addCase(addTrainingProgramAsync.fulfilled, (state, action) => {
        const t = action.payload;
        state.training.push({
          id: t._id || t.id,
          name: t.title,
          category: t.category || 'Technical',
          enrolled: t.enrolledEmployees?.length || 0,
          completed: 0,
          deadline: t.endDate ? new Date(t.endDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '',
          instructor: t.instructor || 'Unknown',
          duration: "10h",
          status: t.status || 'Upcoming'
        });
      })
      .addCase(enrollTrainingAsync.fulfilled, (state, action) => {
        const t = action.payload;
        const index = state.training.findIndex((tr: any) => (tr._id || tr.id) === (t._id || t.id));
        if (index !== -1) {
          state.training[index].enrolledEmployees = t.enrolledEmployees;
          state.training[index].enrolled = t.enrolledEmployees?.length || 0;
        }
      })
      .addCase(updateTrainingProgressAsync.fulfilled, (state, action) => {
        const t = action.payload;
        const index = state.training.findIndex((tr: any) => (tr._id || tr.id) === (t._id || t.id));
        if (index !== -1) {
          state.training[index].enrolledEmployees = t.enrolledEmployees;
        }
      })
.addCase(addCandidateAsync.fulfilled, (state, action) => {
        state.candidates.push(action.payload);
      })
      .addCase(deleteCandidateAsync.fulfilled, (state, action) => {
        state.candidates = state.candidates.filter(
          (c: any) => c.id !== action.payload && c._id !== action.payload,
        );
      })
      .addCase(moveCandidateStageAsync.fulfilled, (state, action) => {
        const payloadCandidate = action.payload.candidate || action.payload;
        const c = state.candidates.find((c: any) => (c._id || c.id) === (payloadCandidate._id || payloadCandidate.id));
        if (c) c.stage = payloadCandidate.stage;

        if (action.payload.onboarding) {
          state.onboarding.push(action.payload.onboarding);
        }
      })
      // Onboarding — new joiner created directly via the "Add New Joiner" form
      .addCase(createOnboardingAsync.fulfilled, (state, action) => {
        state.onboarding.push(action.payload);
      })
      // Onboarding — existing joiner's details edited via the Edit action
      .addCase(updateOnboardingAsync.fulfilled, (state, action) => {
        const idx = state.onboarding.findIndex((o: any) => (o._id || o.id) === (action.payload._id || action.payload.id));
        if (idx !== -1) {
          state.onboarding[idx] = action.payload;
        }
      })
// Onboarding — joiner permanently removed via the Delete action
      .addCase(deleteOnboardingAsync.fulfilled, (state, action) => {
        state.onboarding = state.onboarding.filter((o: any) => o.id !== action.payload && o._id !== action.payload);
      })
      // Onboarding — joiner moved across the pipeline via drag-and-drop
      .addCase(moveOnboardingStageAsync.fulfilled, (state, action) => {
        const j = state.onboarding.find((o: any) => (o._id || o.id) === (action.payload._id || action.payload.id));
        if (j) Object.assign(j, action.payload);
      })
      .addCase(toggleOnboardingTaskAsync.fulfilled, (state, action) => {
        const j = state.onboarding.find((o: any) => (o._id || o.id) === (action.payload._id || action.payload.id));
        if (j) Object.assign(j, action.payload);
      })
      .addCase(toggleHrChecklistAsync.fulfilled, (state, action) => {
        state.onboardingConfig = action.payload;
      })
      .addCase(markNotificationReadAsync.fulfilled, (state, action) => {
        const n = state.notifications.find((n: any) => (n._id || n.id) === (action.payload._id || action.payload.id));
        if (n) n.read = true;
      })
      .addCase(clearNotificationsAsync.fulfilled, (state) => {
        state.notifications = [];
      })
      .addCase(attendanceActionAsync.fulfilled, (state, actionPayload) => {
        const { record, action } = actionPayload.payload;
        const id = record._id || record.id;
        const existingIdx = state.attendance.findIndex((a: any) => a.id === id || a._id === id);

        const formatTime = (d: any) => {
          if (!d) return '--:--';
          return new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        };

        const formatDuration = (secs: number) => {
          const h = Math.floor(secs / 3600);
          const m = Math.floor((secs % 3600) / 60);
          return `${h}h ${m}m`;
        };

        const empId = record.employee?._id || record.employee;
        const emp = state.employees.find((e: any) => e._id === empId || e.id === empId);

        const formattedRecord = {
          id: id,
          _id: id,
          empId: empId,
          name: record.employee?.name || emp?.name || 'Self',
          date: new Date(record.date).toISOString().split('T')[0],
          clockIn: formatTime(record.checkIn),
          clockOut: formatTime(record.checkOut),
          workHrs: '0h 0m',
          breakHrs: formatDuration(record.totalBreakSec || 0),
          status: record.status || 'Present',
          initials: '?',
          avColor: 'blue',
          rawCheckIn: record.checkIn,
          rawCheckOut: record.checkOut,
          breakStart: record.breakStart,
          totalBreakSec: record.totalBreakSec || 0
        };

        if (record.checkIn && record.checkOut) {
          const diffMs = new Date(record.checkOut).getTime() - new Date(record.checkIn).getTime();
          const workSecs = Math.max(0, Math.floor(diffMs / 1000) - (record.totalBreakSec || 0));
          formattedRecord.workHrs = formatDuration(workSecs);
        }

        if (existingIdx !== -1) {
          state.attendance[existingIdx] = { ...state.attendance[existingIdx], ...formattedRecord };
        } else {
          state.attendance.unshift(formattedRecord as any);
        }
      })
      .addCase(runPayrollAsync.fulfilled, (state, action) => {
        state.payroll = action.payload.filter((p: any) => p.employee).map((p: any) => ({
          id: p._id || p.id,
          empId: p.employee?._id || p.employee,
          name: p.employee?.name || 'Unknown',
          dept: p.employee?.dept || 'Engineering',
          month: p.month,
          earnings: p.earnings || [],
          deductions: p.deductions || [],
          grossEarnings: p.grossEarnings || 0,
          totalDeductions: p.totalDeductions || 0,
          net: p.netSalary || 0,
          status: p.status || 'Pending',
          paymentDate: p.paymentDate,
        }));
      })
      .addCase(payPayrollAsync.fulfilled, (state, action) => {
        const updatedPayroll = action.payload;
        const index = state.payroll.findIndex((p: any) => (p._id || p.id) === (updatedPayroll._id || updatedPayroll.id));
        if (index !== -1) {
          state.payroll[index].status = 'Paid';
          state.payroll[index].paymentDate = updatedPayroll.paymentDate;
        }
      })
      .addCase(updateSettingsAsync.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addCase(addOkrAsync.fulfilled, (state, action) => {
        state.okrs.push(action.payload);
      })
      .addCase(updatePerformanceAsync.fulfilled, (state, action) => {
        const updatedPerf = action.payload;
        const index = state.performance.findIndex((p: any) => (p._id || p.id) === (updatedPerf._id || updatedPerf.id));
        if (index !== -1) {
          state.performance[index] = updatedPerf;
        }

        // Recalculate stats
        if (state.performance.length > 0 && state.performanceStats) {
          const totalScore = state.performance.reduce((acc: number, p: any) => acc + (p.score || 0), 0);
          state.performanceStats.avgScore = (totalScore / state.performance.length).toFixed(1);
          state.performanceStats.topPerformers = state.performance.filter((p: any) => p.score >= 90).length;
        }
      })

      .addCase(startNewCycleAsync.fulfilled, (state, action) => {
        state.performance = action.payload;
        state.performanceStats = { avgScore: "0.0", reviewsDone: 0, topPerformers: 0, pendingReviews: action.payload.length };
      })
      // ── Reports ────────────────────────────────────────────────────────
      .addCase(fetchReportAsync.pending, (state) => {
        state.reportLoading = true;
        state.reportData = null;
      })
      .addCase(fetchReportAsync.fulfilled, (state, action) => {
        state.reportLoading = false;
        state.reportData = action.payload;
      })
      .addCase(fetchReportAsync.rejected, (state) => {
        state.reportLoading = false;
      })
      // ── Shifts ────────────────────────────────────────────────────────
      .addCase(fetchShiftsAsync.fulfilled, (state, action) => {
        state.shifts = action.payload;
      })
      .addCase(createShiftAsync.fulfilled, (state, action) => {
        state.shifts.push(action.payload);
      })
      .addCase(updateShiftAsync.fulfilled, (state, action) => {
        const index = state.shifts.findIndex((s: any) => (s._id || s.id) === (action.payload._id || action.payload.id));
        if (index !== -1) {
          state.shifts[index] = action.payload;
        }
      })
      .addCase(deleteShiftAsync.fulfilled, (state, action) => {
        state.shifts = state.shifts.filter(s => s.id !== action.payload && s._id !== action.payload);
      })
      // ── Rosters ────────────────────────────────────────────────────────
      .addCase(fetchRostersAsync.fulfilled, (state, action) => {
        state.rosters = action.payload;
      })
      .addCase(createRosterAsync.fulfilled, (state, action) => {
        state.rosters.push(action.payload);
      })
      .addCase(updateRosterAsync.fulfilled, (state, action) => {
        const index = state.rosters.findIndex((r: any) => (r._id || r.id) === (action.payload._id || action.payload.id));
        if (index !== -1) {
          state.rosters[index] = action.payload;
        }
      })
      .addCase(deleteRosterAsync.fulfilled, (state, action) => {
        state.rosters = state.rosters.filter(r => r.id !== action.payload && r._id !== action.payload);
      });
  },
});

export const { addUser, removeUser, addEmployee, removeEmployee, updateLeaveStatus, addLeaveRequest, addAttendanceRecord, addJob, moveCandidateStage, toggleOnboardingTask, markNotificationRead } = dataSlice.actions;
export default dataSlice.reducer;